import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { questions } from '../data/quiz';
import { results } from '../data/results';
import { brevoForms } from '../data/brevoForms';
import { dominantLevel } from '../utils/scoring';
import type { LevelKey } from '../types/quiz';
import { ProgressBar } from './ProgressBar';
import { QuestionCard } from './QuestionCard';
import { ResultPanel } from './ResultPanel';
import { ResultLocked } from './ResultLocked';
import { RegistrationModal } from './RegistrationModal';
type Phase = 'questions' | 'result';
interface QuizExperienceProps {
  webinarUrl: string;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function QuizExperience({
  webinarUrl
}: QuizExperienceProps) {
  const [phase, setPhase] = useState<Phase>('questions');
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, LevelKey>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const advanceTimer = useRef<number | null>(null);
  const question = questions[index];
  const orderedAnswers = questions.map(item => answers[item.id]).filter((value): value is LevelKey => Boolean(value));
  const level = dominantLevel(orderedAnswers);
  const result = results[level];
  const formConfig = brevoForms[level];
  function handleSelect(key: LevelKey) {
    setAnswers(prev => ({
      ...prev,
      [question.id]: key
    }));
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = window.setTimeout(() => {
      if (index < questions.length - 1) {
        setIndex(prev => prev + 1);
      } else {
        setPhase('result');
        setIsModalOpen(true);
      }
    }, 220);
  }
  function handleBack() {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    setIndex(prev => Math.max(0, prev - 1));
  }
  function handleRestart() {
    setAnswers({});
    setIndex(0);
    setIsRegistered(false);
    setIsModalOpen(false);
    setPhase('questions');
  }
  const showResult = phase === 'result' && isRegistered;
  return <section id="test" className="w-full bg-hueso" aria-label="Test de nivel de consciencia" data-id="element-117" data-mp="b5ocl2aydu9z">
      <div className="mx-auto w-full max-w-lectura px-6 py-16 sm:py-20" data-id="element-118" data-mp="f79ig3p2l8ao">
        {phase === 'questions' && <ProgressBar current={index + 1} total={questions.length} data-id="element-119" data-mp="12wuenoa8i21w" />}

        <div className={phase === 'questions' ? 'mt-10' : ''} data-id="element-120" data-mp="12xncfzr7th7w">
          <AnimatePresence mode="wait" initial={false} data-id="element-121" data-mp="17w6xzz1wcqzq">
            <motion.div key={phase === 'questions' ? `q-${question.id}` : showResult ? 'result' : 'locked'} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} data-id="element-122" data-mp="17tktk8aayrlq">
              {phase === 'questions' && <QuestionCard question={question} selected={answers[question.id]} onSelect={handleSelect} data-id="element-123" data-mp="3jmsuxbf663fi" />}

              {phase === 'result' && !isRegistered && <ResultLocked onOpen={() => setIsModalOpen(true)} data-id="element-124" data-mp="3l36oi8f1jilm" />}

              {showResult && <ResultPanel result={result} webinarUrl={webinarUrl} onRestart={handleRestart} data-id="element-125" data-mp="3g4ln45o1ysvl" />}
            </motion.div>
          </AnimatePresence>
        </div>

        {phase === 'questions' && index > 0 && <button type="button" onClick={handleBack} className="mt-10 inline-flex items-center gap-2 font-sans text-sm text-noche/55 transition-colors duration-150 ease-out hover:text-violeta focus:outline-none focus-visible:ring-2 focus-visible:ring-violeta" data-id="element-126" data-mp="2ngpsr7lyhd1z">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-127" data-mp="29lf0m1z3j61b" />
            Volver a la pregunta anterior
          </button>}
      </div>

      {isModalOpen && formConfig && <RegistrationModal config={formConfig} onClose={() => setIsModalOpen(false)} onSuccess={() => setIsRegistered(true)} data-id="element-128" data-mp="17yuvg2vivj07" />}
    </section>;
}